import React, { useState, useEffect } from "react";
import clsx from "clsx";
import CssBaseline from "@material-ui/core/CssBaseline";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import HomeOutlinedIcon from "@material-ui/icons/HomeOutlined";
import { useHistory, useLocation, useRouteMatch, Link } from "react-router-dom";
import { useDispatch } from "react-redux";
import decode from "jwt-decode";
import styles from "./Header.module.css";
import IconButton from "@material-ui/core/IconButton";
import Button from "@material-ui/core/Button";
import ClickAwayListener from "@material-ui/core/ClickAwayListener";
import Grow from "@material-ui/core/Grow";
import Paper from "@material-ui/core/Paper";
import Popper from "@material-ui/core/Popper";
import MenuItem from "@material-ui/core/MenuItem";
import MenuList from "@material-ui/core/MenuList";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Divider from "@material-ui/core/Divider";
import MenuIcon from "@material-ui/icons/Menu";
import PersonOutlineIcon from "@material-ui/icons/PersonOutline";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import ExitToAppOutlinedIcon from "@material-ui/icons/ExitToAppOutlined";
import VerifiedUserOutlinedIcon from "@material-ui/icons/VerifiedUserOutlined";
import SupervisorAccountIcon from "@material-ui/icons/SupervisorAccount";
import { makeStyles } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Tooltip from "@material-ui/core/Tooltip";
import { Typography } from "@material-ui/core";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogActions from "@material-ui/core/DialogActions";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import VpnKeyOutlinedIcon from "@material-ui/icons/VpnKeyOutlined";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import { useSnackbar } from "../../utils/useSnackbar";
import * as api from "../../api";
import { useNavDrawer } from "../../Context/NavDrawerContext";
import { useEffectiveProfile } from "../../utils/useEffectiveProfile";

const useStyles = makeStyles((theme) => ({
  heading: {
    marginLeft: "auto",
  },
  iconButton: {
    width: 38,
    height: 38,
    borderRadius: 8,
    padding: 7,
    color: "#ffffff",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      transform: "translateY(-1px)",
    },
    [theme.breakpoints.down("xs")]: {
      width: 34,
      height: 34,
      padding: 5,
    },
  },
  avatarButton: {
    minWidth: 44,
    padding: 2,
    borderRadius: "50%",
    transition: "transform 0.15s ease",
    "&:hover": {
      transform: "scale(1.05)",
      backgroundColor: "transparent",
    },
  },
  avatar: {
    width: 36,
    height: 36,
    backgroundColor: "#ffffff",
    color: "#ea580c",
    border: "2px solid rgba(255, 255, 255, 0.9)",
    fontWeight: 800,
    fontSize: "0.95rem",
    boxShadow: "0 2px 6px rgba(0, 0, 0, 0.12)",
    [theme.breakpoints.down("xs")]: {
      width: 32,
      height: 32,
      fontSize: "0.85rem",
    },
  },
  menuPaper: {
    borderRadius: 14,
    overflow: "hidden",
    minWidth: 220,
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 30px rgba(0, 0, 0, 0.15)",
  },
  menuItem: {
    borderRadius: 8,
    padding: "8px 12px",
    margin: "2px 6px",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#fff7ed",
      "& .MuiListItemIcon-root": {
        color: "#ea580c !important",
      },
      "& .MuiListItemText-primary": {
        color: "#ea580c",
        fontWeight: 600,
      },
    },
  },
  logoutMenuItem: {
    borderRadius: 8,
    padding: "8px 12px",
    margin: "2px 6px",
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: "#fee2e2",
      "& .MuiListItemIcon-root": {
        color: "#dc2626 !important",
      },
      "& .MuiListItemText-primary": {
        color: "#dc2626",
        fontWeight: 600,
      },
    },
  },
}));

// Matched top-to-bottom by `useRouteMatch`, which prefix-matches by default:
// always list a nested route above the parent it shares a prefix with.
const PAGE_HEADINGS = {
  "/invoices/credit": "Credit Invoices",
  "/invoices/deleted": "Deleted Invoices",
  "/invoices": "Invoices",
  "/purchases/deleted": "Deleted Purchases",
  "/purchases": "Purchases",
  "/meetings": "Meetings",
  "/settings": "Profile Settings",
  "/dashboard": "Dashboard",
  "/customers": "Customers",
  "/itemsList": "Items",
  "/adminDashboard": "Admin Dashboard",
  // Detail views must sit above their "create" counterparts: `/invoice`
  // prefix-matches `/invoice/:id`, so listing it first would label every
  // invoice detail page "Create Invoice".
  "/invoice/:id": "Invoice Details",
  "/invoice": "Create Invoice",
  "/suppliers": "Suppliers",
  "/stockAdjustments/deleted": "Deleted Stock Adjustments",
  "/stockAdjustments": "Stock Adjustment",
  "/stockDetails": "Stock Summary",
  "/userStats/:userId": "Salesperson Stats",
  "/Webview/PrivacyPolicy": "Privacy Policy",
  "/stats/:invoiceId": "Customer Stats",
  "/staff/commissionSummary": "Commission Summary",
  "/staffstats/:staffId": "Staff Stats",
  "/staff": "Staff",
  "/UnitsList": "Units Management",
  "/bulk-upload": "Bulk Items Upload",
  "/bulk-edit": "Bulk Edit",
  "/supplier-dues": "Supplier Dues",
  "/bulk-image-upload": "Bulk Image Upload",
  "/future-price-list/view": "Scheduled Price Lists",
  "/future-price-list": "Future Price List",
  "/employees": "Employee Master",
  "/shift-logs": "Shift Logs",
  "/purchase/:id": "Purchase Details",
  "/purchase": "Create Purchase",
  "/stockAdjustment/:id": "Stock Adjustment Details",
  "/stockAdjustment": "Create Stock Adjustment",
  "/help": "Help",
  // These routes all existed without an entry here, and the lookup below
  // falls back to "Dashboard" on a miss - so the header actively mislabelled
  // where you were on every one of them. The three /edit/* paths don't
  // prefix-match their create counterparts (/purchase can't match
  // /edit/purchase/:id, which is anchored at the start), hence the separate
  // entries. The two tabbed hubs stay deliberately generic: their tab is in
  // the URL but is also visible on the page itself.
  "/reports/:tab?": "Reports",
  "/goods-returns/:tab?": "Goods Returns",
  "/edit/purchase/:id": "Edit Purchase",
  "/edit/stockAdjustment/:id": "Edit Stock Adjustment",
  "/edit/staff/:id": "Edit Staff",
  "/bulk-stock-adjustment": "Bulk Stock Adjustment",
  "/low-stock": "Low Stock Items",
  "/cash-management": "Cash Management",
  "/program-logs": "Activity Logs",
  "/adminProfiles": "Admin Profiles",
  "/companyLogo": "Company Logo",
  "/statement": "Statement",
  "/admin": "Admin",
};

const Header = () => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(JSON.parse(localStorage.getItem("profile")));
  const history = useHistory();
  const location = useLocation();
  const isSmallScreen = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const match = useRouteMatch(Object.keys(PAGE_HEADINGS));
  const headingText = match ? PAGE_HEADINGS[match.path] : "Dashboard";
  const effectiveProfile = useEffectiveProfile();
  const storeName = effectiveProfile?.businessName || "Store";
  // JSON.parse builds a brand-new object every time, so the old unconditional
  // setUser() here re-rendered Header on *every* navigation even when the
  // stored profile was byte-for-byte identical - and it re-ran the token-expiry
  // effect below (which depends on `user`) along with it. Paired with a page
  // that navigated repeatedly, that was the "Maximum update depth exceeded"
  // crash this line was blamed for. Comparing the raw string keeps the same
  // object identity when nothing actually changed, so the chain stops here.
  const profileRawRef = React.useRef(localStorage.getItem("profile"));
  useEffect(() => {
    const raw = localStorage.getItem("profile");
    if (raw === profileRawRef.current) return;
    profileRawRef.current = raw;
    setUser(raw ? JSON.parse(raw) : null);
  }, [location]);

  const goToPosMainScreen = () => {
    history.push("/PosMainScreen");
  };

  //GET REPO INFO FROM GITHUB
  // useEffect(() => {
  //   getMetaData()
  // },[])

  // const getMetaData = async() => {
  //   const response = await axios.get('https://api.github.com/repos/panshak/arc')
  //       // //(response.data);
  // }

  // Only ever true for a superAdmin who used SuperAdminNavbar.js's "Go to
  // POS" switch - a superAdmin viewing this regular UI at all already means
  // that flag is set (App.js's branch choice is what got them here), so no
  // separate "is currently viewing POS" check is needed on top of this.
  const isSuperAdminUser = Array.isArray(user?.result?.userType)
    ? user.result.userType.includes("superAdmin")
    : user?.result?.userType === "superAdmin";

  const handleGoToSuperAdmin = async () => {
    try {
      await api.recordProgramLogEvent({ event: "SWITCH_TO_SUPERADMIN" });
    } catch (error) {
      // The destination remains available even if the audit service is down.
      console.warn("Could not record the SuperAdmin view switch", error);
    }
    localStorage.removeItem("superAdminViewingPos");
    // Hard navigation, not history.push - App.js decides which shell
    // (SuperAdmin vs regular POS) to render once per page load from that
    // flag, not reactively, so switching back needs a fresh load.
    window.location.href = "/superadmin";
  };

  const logout = () => {
    dispatch({ type: "LOGOUT" });
    setUser(null);
    openSnackbar("Logged out successfully");
    // No reload (was: window.location.href, which forces a full re-fetch/
    // re-parse of the whole JS bundle - white-flash, and would have wiped
    // the toast above before it ever painted). App.js's `user` already
    // derives reactively via useSelector, so a plain history.push is enough
    // - the earlier "two-step race" this avoided only existed between a push
    // AND a reload; removing the reload removes the race, not just the flash.
    history.push("/");
  };

  useEffect(() => {
    const token = user?.token;
    // setUser(JSON.parse(localStorage.getItem('profile')))
    //If token expires, logout the user
    if (token) {
      const decodedToken = decode(token);
      if (decodedToken.exp * 1000 < new Date().getTime()) logout();
    }
    // eslint-disable-next-line
  }, [location, user]); //when location changes, set the user

  const classes = useStyles();
  const [open, setOpen] = React.useState(false);
  const { setMobileNavOpen, desktopNavVisible, setDesktopNavVisible } = useNavDrawer() || {};
  const anchorRef = React.useRef(null);
  // Popper reads anchorEl during render, but a plain ref doesn't re-render
  // when it attaches - and this header returns null entirely on POS screens
  // and when logged out, which unmounts the avatar button (React nulls the
  // ref) while `open` stays true. Coming back then rendered the Popper open
  // against a detached ref, which is the "anchorEl prop is invalid" warning.
  // Mirroring the node into state keeps the two in step; anchorRef is still
  // kept for handleClose's contains() check.
  const [anchorEl, setAnchorEl] = React.useState(null);
  const setAnchorNode = React.useCallback((node) => {
    anchorRef.current = node;
    setAnchorEl(node);
  }, []);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const openLink = (link) => {
    history.push(`/${link}`);
    setOpen(false);
  };

  const [openSnackbar] = useSnackbar();
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const openChangePasswordDialog = () => {
    setOpen(false); // close the avatar menu behind it
    setPasswordDialogOpen(true);
  };

  const closeChangePasswordDialog = () => {
    setPasswordDialogOpen(false);
    setNewPassword("");
    setConfirmPassword("");
    setShowNewPassword(false);
    setShowConfirmPassword(false);
  };

  const handleChangePasswordSubmit = async () => {
    if (!newPassword || newPassword.length < 6) {
      openSnackbar("New password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      openSnackbar("New password and confirmation don't match");
      return;
    }
    setIsChangingPassword(true);
    try {
      await api.changeMyPassword(newPassword);
      openSnackbar("Password updated successfully");
      closeChangePasswordDialog();
    } catch (err) {
      openSnackbar(err?.response?.data?.message || "Failed to update password");
    } finally {
      setIsChangingPassword(false);
    }
  };

  function handleListKeyDown(event) {
    if (event.key === "Tab") {
      event.preventDefault();
      setOpen(false);
    }
  }

  // The menu must not survive a navigation: this header renders nothing at
  // all on POS screens and when logged out, so a menu left open there came
  // back open on the next page - against an anchor button that had been
  // unmounted in the meantime.
  useEffect(() => {
    setOpen(false);
  }, [location]);

  // return focus to the button when we transitioned from !open -> open
  const prevOpen = React.useRef(open);
  React.useEffect(() => {
    // Optional chaining matters here: the effect above closes the menu on
    // navigation, and if that navigation was to a screen where this header
    // hides itself, the button is already gone by the time this runs.
    if (prevOpen.current === true && open === false) {
      anchorRef.current?.focus();
    }

    prevOpen.current = open;
  }, [open]);
  const handleDrawerOpen = () => {
    if (isSmallScreen) {
      if (setMobileNavOpen) setMobileNavOpen((isOpen) => !isOpen);
      return;
    }

    if (setDesktopNavVisible) setDesktopNavVisible((isVisible) => !isVisible);
  };

  // Single source for the tooltip and the accessible name, so the two can
  // never describe different actions.
  const navToggleLabel = isSmallScreen
    ? "Show navigation menu"
    : desktopNavVisible
    ? "Hide navigation bar"
    : "Show navigation bar";

  // A till is usually a touchscreen, where MUI's default 700ms long-press means
  // the tooltip effectively never appears — a tap fires the click first. Showing
  // it immediately on touch (and holding it long enough to read) makes these
  // labels actually reachable on the hardware this runs on.
  const tooltipProps = {
    arrow: true,
    enterDelay: 150,
    enterTouchDelay: 0,
    leaveTouchDelay: 3000,
  };

  if (!user)
    return (
      <div className={styles.header2}>
        {/* <img   onClick={()=> history.push('/')} src="https://sadhanaayurvedics.com/wp-content/uploads/2021/12/5.png" alt="Sadhana Ayurvedics" /> */}
      </div>
    );

  // POS renders AppBarComp instead of this bar. Showing both stacked the
  // page title/avatar on top of the till header and the cart.
  const isPosScreen =
    location.pathname.includes("/PosMainScreen") ||
    location.pathname.startsWith("/edit/invoice");
  if (isPosScreen) return null;

  return (
    <div className={styles.header}>
      <Tooltip title={navToggleLabel} {...tooltipProps}>
        <IconButton
          color="inherit"
          className={classes.iconButton}
          aria-label={navToggleLabel}
          onClick={handleDrawerOpen}
        >
          <MenuIcon style={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Tooltip title="Go to Point of Sale" {...tooltipProps}>
        <IconButton
          color="inherit"
          aria-label="Go to Point of Sale"
          onClick={goToPosMainScreen}
          className={`${classes.iconButton} ${styles.hideHomeOnMobile}`}
        >
          <HomeOutlinedIcon style={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>

      <Link to="/PosMainScreen" className={styles.brand} onClick={goToPosMainScreen}>{storeName}</Link>

      {/* Sizing lives in Header.module.css so it can scale with the viewport;
          a fixed MUI variant would override it at every breakpoint. */}
      <Typography
        component="h1"
        className={`${styles.heading} ${classes.heading}`}
        title={headingText}
      >
        {headingText}
      </Typography>

      <div className={styles.rightSection} >
        <Button
          ref={setAnchorNode}
          aria-controls={open ? "menu-list-grow" : undefined}
          aria-haspopup="true"
          onClick={handleToggle}
          className={classes.avatarButton}
        >
          <Avatar className={classes.avatar}>
            {user?.result?.name?.charAt(0)}
          </Avatar>
        </Button>
        <Popper
          open={open && Boolean(anchorEl)}
          anchorEl={anchorEl}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper elevation={6} className={classes.menuPaper}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={open}
                    id="menu-list-grow"
                    onKeyDown={handleListKeyDown}
                    style={{ padding: 6 }}
                  >
                    <div style={{ padding: "10px 14px 8px", borderBottom: "1px solid #f1f5f9", marginBottom: 4 }}>
                      <Typography style={{ fontSize: "0.875rem", fontWeight: 700, color: "#0f172a", lineHeight: 1.2 }}>
                        {user?.result?.name}
                      </Typography>
                      <Typography style={{ fontSize: "0.75rem", color: "#64748b", marginTop: 2 }}>
                        {user?.result?.email || (user?.result?.role ? `Role: ${user.result.role}` : "Active User")}
                      </Typography>
                    </div>
                    {isSuperAdminUser && (
                      <>
                        <MenuItem onClick={handleGoToSuperAdmin} className={classes.menuItem}>
                          <ListItemIcon style={{ minWidth: 32 }}>
                            <SupervisorAccountIcon fontSize="small" style={{ color: "#7c3aed" }} />
                          </ListItemIcon>
                          <ListItemText
                            primary="Go to SuperAdmin Panel"
                            primaryTypographyProps={{ style: { fontSize: "0.8125rem", fontWeight: 600, color: "#7c3aed" } }}
                          />
                        </MenuItem>
                        <Divider style={{ margin: "6px 4px" }} />
                      </>
                    )}
                    <MenuItem onClick={() => openLink("settings")} className={classes.menuItem}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <PersonOutlineIcon fontSize="small" style={{ color: "#ea580c" }} />
                      </ListItemIcon>
                      <ListItemText primary="Profile Settings" primaryTypographyProps={{ style: { fontSize: "0.8125rem", fontWeight: 500 } }} />
                    </MenuItem>
                    <MenuItem onClick={() => openLink("help")} className={classes.menuItem}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <HelpOutlineIcon fontSize="small" style={{ color: "#ea580c" }} />
                      </ListItemIcon>
                      <ListItemText primary="Help" primaryTypographyProps={{ style: { fontSize: "0.8125rem", fontWeight: 500 } }} />
                    </MenuItem>
                    <MenuItem onClick={openChangePasswordDialog} className={classes.menuItem}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <VpnKeyOutlinedIcon fontSize="small" style={{ color: "#ea580c" }} />
                      </ListItemIcon>
                      <ListItemText primary="Change Password" primaryTypographyProps={{ style: { fontSize: "0.8125rem", fontWeight: 500 } }} />
                    </MenuItem>
                    <MenuItem onClick={() => openLink("Webview/PrivacyPolicy")} className={classes.menuItem}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <VerifiedUserOutlinedIcon fontSize="small" style={{ color: "#ea580c" }} />
                      </ListItemIcon>
                      <ListItemText primary="Privacy Policy" primaryTypographyProps={{ style: { fontSize: "0.8125rem", fontWeight: 500 } }} />
                    </MenuItem>
                    <Divider style={{ margin: "6px 4px" }} />
                    <MenuItem onClick={() => logout()} className={classes.logoutMenuItem}>
                      <ListItemIcon style={{ minWidth: 32 }}>
                        <ExitToAppOutlinedIcon fontSize="small" style={{ color: "#dc2626" }} />
                      </ListItemIcon>
                      <ListItemText primary="Logout" primaryTypographyProps={{ style: { color: "#dc2626", fontSize: "0.8125rem", fontWeight: 600 } }} />
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </div>

      <Dialog
        open={passwordDialogOpen}
        onClose={closeChangePasswordDialog}
        maxWidth="xs"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, overflow: "hidden" } }}
      >
        <DialogTitle style={{ backgroundColor: "#ea580c", backgroundImage: "linear-gradient(135deg, #f97316 0%, #ea580c 100%)", color: "#fff", display: "flex", alignItems: "center", gap: 10, padding: "16px 24px" }}>
          <VpnKeyOutlinedIcon fontSize="small" />
          <span style={{ fontWeight: 700, fontSize: "1.05rem" }}>Change Password</span>
        </DialogTitle>
        <DialogContent style={{ padding: 24, paddingTop: 24, display: "flex", flexDirection: "column", gap: 16 }}>
          <TextField
            autoFocus
            label="New Password"
            variant="outlined"
            size="small"
            type={showNewPassword ? "text" : "password"}
            fullWidth
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            inputProps={{ minLength: 6 }}
            helperText="At least 6 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end" size="small">
                    {showNewPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <TextField
            label="Confirm New Password"
            variant="outlined"
            size="small"
            type={showConfirmPassword ? "text" : "password"}
            fullWidth
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end" size="small">
                    {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions style={{ padding: "12px 24px 20px", gap: 8 }}>
          <Button onClick={closeChangePasswordDialog} style={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
            Cancel
          </Button>
          <Button
            onClick={handleChangePasswordSubmit}
            variant="contained"
            style={{
              backgroundColor: "#ea580c",
              backgroundImage: "linear-gradient(135deg, #ea580c 0%, #f97316 100%)",
              color: "#fff",
              textTransform: "none",
              fontWeight: 700,
              borderRadius: 8,
              boxShadow: "0 2px 6px rgba(234, 88, 12, 0.25)",
            }}
            disabled={isChangingPassword}
          >
            {isChangingPassword ? "Updating..." : "Update Password"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Header;
